import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  QrCode, RefreshCw, ShieldOff, MapPin, Building2, Loader2,
  CheckCircle, AlertTriangle, Search, ChevronDown, ChevronUp, Plus,
  Pencil, Archive, ArchiveRestore,
} from 'lucide-react';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import API_BASE from '../../utils/apiBase';
import { CARE_LOCATION_TYPE_OPTIONS } from '../../config/tenantSetupOptions';

const API = API_BASE;

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const QR_WIDTH_PX = 280;

export default function CareLocationsPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Per-location state stored as maps: locationId → value
  const [qrTokens, setQrTokens] = useState({});    // { token, active, at, by }
  const [qrImages, setQrImages] = useState({});    // PNG data URL
  const [generating, setGenerating] = useState({}); // bool
  const [deactivating, setDeactivating] = useState({}); // bool
  const [expanded, setExpanded] = useState({});    // bool — show QR panel

  // Create care location
  const [organisations, setOrganisations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', location_type: '', organisation_id: '',
    address_line_1: '', city: '', postcode: '',
  });

  // Archive / restore per-location
  const [archiving, setArchiving] = useState({});
  const [restoring, setRestoring] = useState({});

  // Edit care location
  const [showEdit, setShowEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', location_type: '', organisation_id: '',
    address_line_1: '', city: '', postcode: '',
  });

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/care-locations?include_inactive=${includeInactive}`,
        { headers },
      );
      setLocations(res.data?.care_locations || []);
    } catch {
      toast.error('Failed to load care locations');
    } finally {
      setLoading(false);
    }
  }, [headers, includeInactive]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/organisations?include_inactive=false`, { headers });
      setOrganisations(res.data?.organisations || []);
    } catch {
      // non-fatal
    }
  }, [headers]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: createForm.name.trim(),
        location_type: createForm.location_type,
        address_line_1: createForm.address_line_1.trim(),
        city: createForm.city.trim(),
        postcode: createForm.postcode.trim(),
        organisation_id: createForm.organisation_id || null,
      };
      // Duplicate check: warn if same name + postcode already exists as active
      const dupName = payload.name.toLowerCase();
      const dupPostcode = payload.postcode.replace(/\s+/g, '').toLowerCase();
      const duplicate = locations.find(
        (l) =>
          l.is_active !== false &&
          l.name?.toLowerCase() === dupName &&
          l.postcode?.replace(/\s+/g, '').toLowerCase() === dupPostcode,
      );
      if (duplicate) {
        toast.error(
          `"${duplicate.name}" (${duplicate.postcode}) already exists. Archive the duplicate or use a different name/postcode.`,
        );
        setCreating(false);
        return;
      }
      await axios.post(`${API}/care-locations`, payload, { headers });
      toast.success('Care location created');
      setShowCreate(false);
      setCreateForm({ name: '', location_type: '', organisation_id: '', address_line_1: '', city: '', postcode: '' });
      fetchLocations();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[CareLocations] create failed', err.response?.status, err.response?.data);
      const detail = err.response?.data?.detail;
      let msg = 'Failed to create care location';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic v2 validation error array — extract first message
        const first = detail[0];
        msg = first?.msg ?? JSON.stringify(first);
      } else if (!err.response) {
        msg = 'Network error — could not reach server';
      } else if (err.response.status) {
        msg = `Server error (${err.response.status}) — check console for details`;
      }
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = useCallback(async (locationId) => {
    setArchiving(prev => ({ ...prev, [locationId]: true }));
    try {
      await axios.post(`${API}/care-locations/${locationId}/archive`, {}, { headers });
      toast.success('Care location archived');
      fetchLocations();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to archive care location');
    } finally {
      setArchiving(prev => ({ ...prev, [locationId]: false }));
    }
  }, [headers, fetchLocations]);

  const handleRestore = useCallback(async (locationId) => {
    setRestoring(prev => ({ ...prev, [locationId]: true }));
    try {
      await axios.post(`${API}/care-locations/${locationId}/restore`, {}, { headers });
      toast.success('Care location restored');
      fetchLocations();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to restore care location');
    } finally {
      setRestoring(prev => ({ ...prev, [locationId]: false }));
    }
  }, [headers, fetchLocations]);

  const openEdit = useCallback((loc) => {
    setEditTarget(loc);
    setEditForm({
      name: loc.name || '',
      location_type: loc.location_type || '',
      organisation_id: loc.organisation_id || '',
      address_line_1: loc.address_line_1 || '',
      city: loc.city || '',
      postcode: loc.postcode || '',
    });
    setShowEdit(true);
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditing(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        location_type: editForm.location_type,
        address_line_1: editForm.address_line_1.trim(),
        city: editForm.city.trim(),
        postcode: editForm.postcode.trim(),
        organisation_id: editForm.organisation_id || null,
      };
      await axios.put(`${API}/care-locations/${editTarget.id}`, payload, { headers });
      toast.success('Care location updated');
      setShowEdit(false);
      setEditTarget(null);
      fetchLocations();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[CareLocations] update failed', err.response?.status, err.response?.data);
      const detail = err.response?.data?.detail;
      let msg = 'Failed to update care location';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) msg = detail[0]?.msg ?? JSON.stringify(detail[0]);
      toast.error(msg);
    } finally {
      setEditing(false);
    }
  };

  const buildQrImage = useCallback(async (rawToken) => {
    return QRCode.toDataURL(rawToken, { width: QR_WIDTH_PX, margin: 2, errorCorrectionLevel: 'M' });
  }, []);

  const loadExistingToken = useCallback(async (locationId) => {
    try {
      const res = await axios.get(`${API}/care-locations/${locationId}/qr-token`, { headers });
      const { site_qr_token, site_qr_token_active, site_qr_token_at, site_qr_token_by } = res.data;
      setQrTokens(prev => ({
        ...prev,
        [locationId]: { token: site_qr_token, active: site_qr_token_active, at: site_qr_token_at, by: site_qr_token_by },
      }));
      if (site_qr_token) {
        const img = await buildQrImage(site_qr_token);
        setQrImages(prev => ({ ...prev, [locationId]: img }));
      }
      setExpanded(prev => ({ ...prev, [locationId]: true }));
    } catch (err) {
      if (err.response?.status === 404) {
        toast('No QR token has been generated for this location yet.');
      } else {
        toast.error('Failed to load QR token');
      }
    }
  }, [headers, buildQrImage]);

  const generateQR = useCallback(async (locationId) => {
    setGenerating(prev => ({ ...prev, [locationId]: true }));
    try {
      const res = await axios.post(
        `${API}/care-locations/${locationId}/generate-qr-token`,
        {},
        { headers },
      );
      const { site_qr_token, site_qr_token_active, site_qr_token_at, site_qr_token_by } = res.data;
      setQrTokens(prev => ({
        ...prev,
        [locationId]: { token: site_qr_token, active: site_qr_token_active, at: site_qr_token_at, by: site_qr_token_by },
      }));
      const img = await buildQrImage(site_qr_token);
      setQrImages(prev => ({ ...prev, [locationId]: img }));
      setExpanded(prev => ({ ...prev, [locationId]: true }));
      toast.success('QR token generated — print and display it at the care location.');
    } catch {
      toast.error('Failed to generate QR token');
    } finally {
      setGenerating(prev => ({ ...prev, [locationId]: false }));
    }
  }, [headers, buildQrImage]);

  const deactivateToken = useCallback(async (locationId) => {
    setDeactivating(prev => ({ ...prev, [locationId]: true }));
    try {
      await axios.post(`${API}/care-locations/${locationId}/deactivate-qr-token`, {}, { headers });
      setQrTokens(prev => ({
        ...prev,
        [locationId]: { ...(prev[locationId] || {}), active: false },
      }));
      toast.success('QR token deactivated. Workers can no longer verify using the old code.');
    } catch {
      toast.error('Failed to deactivate token');
    } finally {
      setDeactivating(prev => ({ ...prev, [locationId]: false }));
    }
  }, [headers]);

  const printQrPdf = useCallback(async (loc, locationId) => {
    const dataUrl = qrImages[locationId];
    if (!dataUrl) {
      toast.error('Generate a QR code first');
      return;
    }
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Header
      pdf.setFillColor(30, 64, 175); // Osabea blue
      pdf.rect(0, 0, 210, 22, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Osabea Care — Site Clock-In QR Code', 105, 14, { align: 'center' });

      // Location name
      pdf.setTextColor(30, 30, 30);
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text(loc.name, 105, 38, { align: 'center' });

      // Address
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`${loc.address_line_1}, ${loc.city}`, 105, 48, { align: 'center' });
      pdf.text(loc.postcode, 105, 55, { align: 'center' });

      // QR code centred
      pdf.addImage(dataUrl, 'PNG', 55, 65, 100, 100);

      // Instructions
      pdf.setFontSize(13);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 30, 30);
      pdf.text('How to use', 105, 177, { align: 'center' });

      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.text('1. Open the Osabea worker app and select your shift.', 105, 185, { align: 'center' });
      pdf.text('2. Tap "Clock In" or "Clock Out".', 105, 192, { align: 'center' });
      pdf.text('3. Point your camera at this QR code when prompted.', 105, 199, { align: 'center' });
      pdf.text('4. If the camera is unavailable, tap "Enter code manually" and type:', 105, 206, { align: 'center' });

      // Manual code (truncated for readability on poster)
      const tokenData = qrTokens[locationId];
      if (tokenData?.token) {
        pdf.setFont(undefined, 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(30, 64, 175);
        pdf.text(tokenData.token, 105, 214, { align: 'center' });
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont(undefined, 'normal');
      pdf.text(
        `Generated ${formatDateTime(tokenData?.at)} — Keep secure. Replace immediately if lost or compromised.`,
        105, 224, { align: 'center' },
      );

      pdf.save(`QR-Poster-${loc.name.replace(/\s+/g, '-')}.pdf`);
      toast.success('QR poster downloaded as PDF');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  }, [qrImages, qrTokens]);

  const filteredLocations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.city?.toLowerCase().includes(q) ||
        l.postcode?.toLowerCase().includes(q) ||
        l.address_line_1?.toLowerCase().includes(q),
    );
  }, [locations, search]);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Create care location dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Care Location</DialogTitle>
            <DialogDescription>Create a new care location. You can generate a QR poster for it afterwards.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-1">
            <div className="space-y-2">
              <Label htmlFor="cl-name">Name *</Label>
              <Input
                id="cl-name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="e.g. Sunridge Care Home"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cl-type">Location Type *</Label>
                <select
                  id="cl-type"
                  value={createForm.location_type}
                  onChange={(e) => setCreateForm({ ...createForm, location_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                >
                  <option value="">Select…</option>
                  {CARE_LOCATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cl-org">Organisation</Label>
                <select
                  id="cl-org"
                  value={createForm.organisation_id}
                  onChange={(e) => setCreateForm({ ...createForm, organisation_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">— None —</option>
                  {organisations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl-addr">Address Line 1 *</Label>
              <Input
                id="cl-addr"
                value={createForm.address_line_1}
                onChange={(e) => setCreateForm({ ...createForm, address_line_1: e.target.value })}
                placeholder="e.g. 12 Oak Street"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cl-city">City *</Label>
                <Input
                  id="cl-city"
                  value={createForm.city}
                  onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                  placeholder="e.g. Birmingham"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cl-postcode">Postcode *</Label>
                <Input
                  id="cl-postcode"
                  value={createForm.postcode}
                  onChange={(e) => setCreateForm({ ...createForm, postcode: e.target.value })}
                  placeholder="e.g. B1 1AA"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create Care Location'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit care location dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { if (!open) { setShowEdit(false); setEditTarget(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Care Location</DialogTitle>
            <DialogDescription>Update the details for {editTarget?.name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 pt-1">
            <div className="space-y-2">
              <Label htmlFor="ed-name">Name *</Label>
              <Input
                id="ed-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ed-type">Location Type *</Label>
                <select
                  id="ed-type"
                  value={editForm.location_type}
                  onChange={(e) => setEditForm({ ...editForm, location_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                >
                  <option value="">Select…</option>
                  {CARE_LOCATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ed-org">Organisation</Label>
                <select
                  id="ed-org"
                  value={editForm.organisation_id}
                  onChange={(e) => setEditForm({ ...editForm, organisation_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">— None —</option>
                  {organisations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ed-addr">Address Line 1 *</Label>
              <Input
                id="ed-addr"
                value={editForm.address_line_1}
                onChange={(e) => setEditForm({ ...editForm, address_line_1: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ed-city">City *</Label>
                <Input
                  id="ed-city"
                  value={editForm.city}
                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ed-postcode">Postcode *</Label>
                <Input
                  id="ed-postcode"
                  value={editForm.postcode}
                  onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowEdit(false); setEditTarget(null); }}
                disabled={editing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={editing}>
                {editing ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Page header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Care Locations</h1>
          <p className="text-sm text-text-muted mt-1">
            Manage care locations and generate QR site verification tokens for attendance tracking.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Care Location
          </Button>
          <Button variant="outline" onClick={fetchLocations} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Search by name, city or postcode…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded border-slate-300"
          />
          Show archived
        </label>
      </div>

      {/* Location list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : filteredLocations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-text-muted space-y-3">
            {locations.length === 0 ? (
              <>
                <p className="font-medium text-text-primary">No care locations yet</p>
                <p className="text-sm">Create a care location before generating QR posters.</p>
                <Button className="mt-1" onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Care Location
                </Button>
              </>
            ) : (
              'No locations match your search.'
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredLocations.map((loc) => {
            const tokenInfo = qrTokens[loc.id];
            const imgUrl = qrImages[loc.id];
            const isGenerating = generating[loc.id];
            const isDeactivating = deactivating[loc.id];
            const isExpanded = expanded[loc.id];

            return (
              <Card
                key={loc.id}
                className={loc.is_active === false ? 'opacity-60 border-dashed' : ''}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 flex-1 min-w-0">
                      <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                        <Building2 className="h-4 w-4 text-primary shrink-0" />
                        <span className="truncate">{loc.name}</span>
                        {loc.is_active === false && (
                          <Badge variant="outline" className="text-slate-400 shrink-0">Archived</Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-text-muted flex items-center gap-1">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {loc.address_line_1}, {loc.city}, {loc.postcode}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {tokenInfo?.active ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> QR Active
                        </Badge>
                      ) : tokenInfo && !tokenInfo.active ? (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">
                          <AlertTriangle className="h-3 w-3 mr-1" /> QR Inactive
                        </Badge>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => openEdit(loc)}
                        aria-label="Edit care location"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {loc.is_active === false ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                          onClick={() => handleRestore(loc.id)}
                          disabled={restoring[loc.id]}
                          aria-label="Restore care location"
                          title="Restore"
                        >
                          {restoring[loc.id]
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <ArchiveRestore className="h-4 w-4" />}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-amber-600"
                          onClick={() => handleArchive(loc.id)}
                          disabled={archiving[loc.id]}
                          aria-label="Archive care location"
                          title="Archive"
                        >
                          {archiving[loc.id]
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Archive className="h-4 w-4" />}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setExpanded(prev => ({ ...prev, [loc.id]: !prev[loc.id] }))}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4" />
                          : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="space-y-4 pt-0">
                    <hr className="border-slate-100" />

                    {/* QR image + metadata */}
                    {imgUrl ? (
                      <div className="flex flex-col sm:flex-row items-start gap-4">
                        <div className="flex flex-col items-center gap-2">
                          <img
                            src={imgUrl}
                            alt={`QR code for ${loc.name}`}
                            className="w-40 h-40 border rounded-lg bg-white p-1"
                          />
                          {!tokenInfo?.active && (
                            <p className="text-xs text-amber-700 font-medium text-center max-w-[10rem]">
                              Token inactive — workers cannot verify
                            </p>
                          )}
                        </div>
                        <div className="space-y-1 text-sm">
                          {tokenInfo?.at && (
                            <p className="text-text-muted">
                              <span className="font-medium">Generated:</span>{' '}
                              {formatDateTime(tokenInfo.at)}
                            </p>
                          )}
                          <p className="text-text-muted text-xs break-all">
                            <span className="font-medium">Token (manual entry):</span>{' '}
                            <span className="font-mono">{tokenInfo?.token}</span>
                          </p>
                          <p className="text-xs text-slate-400 mt-2">
                            Print the QR poster and display it at the care location entrance.
                            Workers scan it when clocking in and out.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        onClick={() => loadExistingToken(loc.id)}
                      >
                        <QrCode className="h-4 w-4 mr-2" />
                        Load existing QR token
                      </Button>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => generateQR(loc.id)}
                        disabled={isGenerating}
                      >
                        {isGenerating
                          ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          : <QrCode className="h-4 w-4 mr-2" />}
                        {tokenInfo ? 'Rotate QR Token' : 'Generate QR Token'}
                      </Button>

                      {imgUrl && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printQrPdf(loc, loc.id)}
                        >
                          Print / Download QR Poster
                        </Button>
                      )}

                      {tokenInfo?.active && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deactivateToken(loc.id)}
                          disabled={isDeactivating}
                        >
                          {isDeactivating
                            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            : <ShieldOff className="h-4 w-4 mr-2" />}
                          Deactivate Token
                        </Button>
                      )}
                    </div>

                    {/* Rotate warning */}
                    {tokenInfo?.active && (
                      <p className="text-xs text-slate-400">
                        Rotating generates a new token immediately — the old QR poster will stop working.
                        Update the poster before rotating.
                      </p>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
